// src/utils/chapter13Calculator.js
// Chapter 13 plan calculator utility

/**
 * Amortize a principal over `months` at an annual interest rate `annualRate`.
 * Returns total of all payments (monthlyPayment * months).
 */
import { computeDisposableIncome } from './disposableIncomeCalculator'
import { computeLiquidationValue } from '../consultation.js'
// JSON mapping of districts to counties
import districtData from '../json/district_divisions_counties.json';
// JSON mapping of districts to admin expense multipliers by state
import adminData from '../json/nested_county_district_admin_expense.json';
import { getChapter13TotalAttorneyFee } from '../utils/attorneyFeeSettings';
export function amortizedTotal(principal, annualRate, months) {
    const r = annualRate / 12;
    const m = principal * (r / (1 - (1 + r) ** -months));
    return m * months;
  }
  
  /**
   * Compute a Chapter 13 plan payment and breakdown.
   *
   * @param {Object} answers - questionnaire answers
   * @param {number} answers.planLength        - 36 or 60
   * @param {Object} answers.mortgage          - { payment, monthsBehind, includeInPlan }
   * @param {Array<Object>} answers.vehicles   - list of { loanBalance, currentPayment, monthsBehind, keepInPlan }
   * @param {number} answers.upfrontFee        - upfront attorney fee paid      - court filing fee
   * @param {Array<Object>} answers.backTaxDebtDetails - [{ year, amount }, ...]
   * @param {number} answers.liquidationTotal
   * @param {Object} [options]
   *
   * @returns {Object} breakdown
   */
  export function computeChapter13Plan(answers, planLength, options = {}) {
    const {
      mortgage = {},
      vehicles = [],
      upfrontFee = 0,
      filingFee = 313,

    } = answers;
  

    const fips = answers.censusTract.STATE + answers.censusTract.COUNTY
    console.log("fips: " + fips)
    

    // look up district based on the fips code
    const district = Object.keys(districtData).find(
      key => districtData[key].counties && districtData[key].counties[fips]
    );
    console.log('District for fips', fips, 'is', district);
    // derive admin expense multiplier key as uppercase district code without trailing letter
    const adminKey = district ? district.toUpperCase().slice(0, -1) : null;
    let adminExpenseMultiplier = null;
    if (adminKey) {
      // search each state section for our district code key
      for (const stateName of Object.keys(adminData)) {
        const stateSection = adminData[stateName];
        if (stateSection && stateSection[adminKey]) {
          adminExpenseMultiplier = stateSection[adminKey].admin_expense_multiplier;
          break;
        }
      }
    }
    console.log('Admin expense multiplier for district', district, 'is', adminExpenseMultiplier);
    const trusteeFactor = 1 + (adminExpenseMultiplier/100)
    console.log("trustee factor: " + trusteeFactor)
  
    // 1. Secured creditors total
    let securedTotal = 0;
  
    
    // 1.A Mortgage payments through plan
    const mortgageItems = [];

    // Primary mortgage (always include, mark included only if plan-eligible)
    if (answers.ownsHome === 'yes') {
      const principalTotal = answers.mortgageMonthlyPayment * planLength;
      const arrearageTotal = answers.mortgageMonthlyPayment * (answers.mortgageMonthsBehind || 0);
      const included = answers.hasMortgage === 'yes' && answers.mortgagePaymentStatus === 'no';
      let street = answers.street;
      if (answers.street === "")
      {
        street = "Primary Residence"
      }
      mortgageItems.push({
        name: street,
        creditor: answers.mortgageCreditor,
        included,
        monthlyPayment: answers.mortgageMonthlyPayment,
        totalPrincipal: principalTotal,
        totalArrearage: arrearageTotal,
        total: principalTotal + arrearageTotal,
      });
    }

    // Second mortgage (always include, mark included only if plan-eligible)
    if (answers.ownsHome === 'yes') {
      const principalTotal = answers.secondMortgageMonthlyPayment * planLength;
      const arrearageTotal = answers.secondMortgageMonthlyPayment * (answers.secondMortgageMonthsBehind || 0);
      const included = answers.hasSecondMortgage === 'yes' && answers.secondMortgagePaymentStatus === 'no';
      let street = answers.street;
      if (answers.street === "")
        {
          street = "Primary Residence"
        }
      if (answers.hasSecondMortgage === 'yes'){
        mortgageItems.push({
            name: street,
            creditor: answers.secondMortgageCreditor,
            included,
            monthlyPayment: answers.secondMortgageMonthlyPayment,
            totalPrincipal: principalTotal,
            totalArrearage: arrearageTotal,
            total: principalTotal + arrearageTotal,
          });
      }

    }

    // Other real estate properties
    answers.otherRealEstateProperties.forEach(m => {
      // Primary mortgage always added
      const principalTotal = m.mortgageMonthlyPayment * planLength;
      const arrearageTotal = m.mortgageMonthlyPayment * (m.mortgageMonthsBehind || 0);
      const included = m.hasMortgage === 'yes' && m.mortgagePaymentStatus === 'no';
      mortgageItems.push({
        name: m.street,
        creditor: m.mortgageCreditor,
        included,
        monthlyPayment: m.mortgageMonthlyPayment,
        totalPrincipal: principalTotal,
        totalArrearage: arrearageTotal,
        total: principalTotal + arrearageTotal,
      });
      // Second mortgage always added
      const principalTotal2 = m.secondMortgageMonthlyPayment * planLength;
      const arrearageTotal2 = m.secondMortgageMonthlyPayment * (m.secondMortgageMonthsBehind || 0);
      const included2 = m.hasSecondMortgage === 'yes' && m.secondMortgagePaymentStatus === 'no';
      if (m.hasSecondMortgage === 'yes'){
        mortgageItems.push({
            name: m.street,
            creditor: m.secondMortgageCreditor,
            included: included2,
            monthlyPayment: m.secondMortgageMonthlyPayment,
            totalPrincipal: principalTotal2,
            totalArrearage: arrearageTotal2,
            total: principalTotal2 + arrearageTotal2,
          });
      }

    });

    // Spouse's other real estate
    answers.spouseOtherRealEstateProperties.forEach(m => {
      // Primary mortgage always added
      const principalTotal = m.mortgageMonthlyPayment * planLength;
      const arrearageTotal = m.mortgageMonthlyPayment * (m.mortgageMonthsBehind || 0);
      const included = m.hasMortgage === 'yes' && m.mortgagePaymentStatus === 'no';
      mortgageItems.push({
        name: m.street,
        creditor: m.mortgageCreditor,
        included,
        monthlyPayment: m.mortgageMonthlyPayment,
        totalPrincipal: principalTotal,
        totalArrearage: arrearageTotal,
        total: principalTotal + arrearageTotal,
      });
      // Second mortgage always added
      const principalTotal2 = m.secondMortgageMonthlyPayment * planLength;
      const arrearageTotal2 = m.secondMortgageMonthlyPayment * (m.secondMortgageMonthsBehind || 0);
      const included2 = m.hasSecondMortgage === 'yes' && m.secondMortgagePaymentStatus === 'no';
      if (m.hasSecondMortgage === 'yes'){
        mortgageItems.push({
            name: m.street,
            creditor: m.secondMortgageCreditor,
            included: included2,
            monthlyPayment: m.secondMortgageMonthlyPayment,
            totalPrincipal: principalTotal2,
            totalArrearage: arrearageTotal2,
            total: principalTotal2 + arrearageTotal2,
          });
      }

    });

    // Sum only those mortgage items where included === true
    const mortgageTotalInPlan = mortgageItems
      .filter(item => item.included)
      .reduce((sum, item) => sum + item.total, 0);
    securedTotal += mortgageTotalInPlan;
  
    // 1.B Vehicles
    const vehicleItems = [];
    answers.vehicles.forEach(v => {
      // Maintenance: pay your regular payment each month + catch up arrears
      const maintenancePrincipal = (v.monthlyPayment || 0) * planLength;
      const maintenanceArrearage = (v.monthlyPayment || 0) * (v.monthsBehind || 0);
      const maintenanceTotal = maintenancePrincipal + maintenanceArrearage;
    
      // Cramdown: amortize the full balance over the plan at TILL RATE APR
      const cramdownTotal = amortizedTotal(v.loanBalance, 0.085, planLength);
      const cramdownMonthly = cramdownTotal / planLength;
      console.log("cramdownMonthly: " + cramdownMonthly);
      let treatment = 'surrender';
      let principal = 0;
      let arrearage = 0;
      const included = (v.currentOnPayments === 'no' || cramdownMonthly*trusteeFactor < v.monthlyPayment || !(v.keep === "keep"))
      if (v.keep === "keep") {
        if (maintenanceTotal < cramdownTotal) {
            treatment = 'maintenance'
            principal = maintenancePrincipal;
            arrearage = maintenanceArrearage;
        }
        else {
            treatment = 'cramdown'
            principal = cramdownTotal;
            arrearage = 0;
        }
      }
      if (v.loan === "loan" || v.loanBalance != ""){
      vehicleItems.push({
        description:`${v.year} ${v.make} ${v.model}`,
        creditor: v.creditor,
        included:included,
        monthlyPayment:v.monthlyPayment,
        treatment: treatment,
        totalPrincipal: principal,
        totalArrearage: arrearage,
        total: principal + arrearage,
      })
      }
    });
    answers.spouseVehicles.forEach(v => {
        // Maintenance: pay your regular payment each month + catch up arrears
        const maintenancePrincipal = (v.monthlyPayment || 0) * planLength;
        const maintenanceArrearage = (v.monthlyPayment || 0) * (v.monthsBehind || 0);
        const maintenanceTotal = maintenancePrincipal + maintenanceArrearage;
      
        // Cramdown: amortize the full balance over the plan at TILL RATE APR
        const cramdownTotal = amortizedTotal(v.loanBalance, 0.085, planLength);
        const cramdownMonthly = cramdownTotal / planLength;
        console.log("cramdownMonthly: " + cramdownMonthly);
        let treatment = 'surrender';
        let principal = 0;
        let arrearage = 0;
        const included = (v.currentOnPayments === 'no' || cramdownMonthly*trusteeFactor < v.monthlyPayment || !(v.keep === "keep"))
        if (v.keep === "keep") {
          if (maintenanceTotal < cramdownTotal) {
              treatment = 'maintenance'
              principal = maintenancePrincipal;
              arrearage = maintenanceArrearage;
          }
          else {
              treatment = 'cramdown'
              principal = cramdownTotal;
              arrearage = 0;
          }
        }
        if (v.loan === "loan" || v.loanBalance != ""){
        vehicleItems.push({
          description:`${v.year} ${v.make} ${v.model}`,
          creditor: v.creditor,
          included:included,
          monthlyPayment:v.monthlyPayment,
          treatment: treatment,
          totalPrincipal: principal,
          totalArrearage: arrearage,
          total: principal + arrearage,
        })
        }
      });
    const vehicleTotalInPlan = vehicleItems
      .filter(item => item.included)
      .reduce((sum, item) => sum + item.total, 0);
    securedTotal += vehicleTotalInPlan;
  

    // 2. Priority debts in full
    const totalAttorneyFee = getChapter13TotalAttorneyFee(answers);
    const attorneyBalance = Math.max(totalAttorneyFee - Number(upfrontFee || 0), 0);


    const nowYear = new Date().getFullYear();
    const respondentTaxLast3 = (answers.backTaxDebtDetails || [])
      .filter(d => Number(d.year) >= nowYear - 3 && Number(d.year) < nowYear)
      .reduce((sum, d) => sum + Number(d.amount), 0);
    const spouseTaxLast3 = (answers.spouseBackTaxDebtDetails || [])
      .filter(d => Number(d.year) >= nowYear - 3 && Number(d.year) < nowYear)
      .reduce((sum, d) => sum + Number(d.amount), 0);
    const taxLast3Years = respondentTaxLast3 + spouseTaxLast3;

    const respondendentChildSupportArrears = (Number(answers.childSupportArrearageAmount) || 0)
    const spouseChildSupportArrears = (Number(answers.spouseChildSupportArrearageAmount) || 0)
    const childSupportArrears =
      (Number(answers.childSupportArrearageAmount) || 0) +
      (Number(answers.spouseChildSupportArrearageAmount) || 0);

    const priorityTotal =
      attorneyBalance +
      filingFee +
      taxLast3Years +
      childSupportArrears;

    const priorityItems = {
        respondentTaxLast3,
        spouseTaxLast3,
        filingFee,
        respondendentChildSupportArrears,
        spouseChildSupportArrears
    }
  
    // 3. Unsecured "best interest"
    const creditCardTotal = (answers.creditCardDebtDetails || []).reduce((sum,d) => sum + Number(d.debtBalance || 0), 0)
    const spouseCreditCardTotal = (answers.spouseCreditCardDebtDetails || []).reduce((sum,d) => sum + Number(d.debtBalance || 0), 0)
    const personalLoanTotal = (answers.personalLoanDetails || []).reduce((sum,d) => sum + Number(d.debtBalance || 0), 0)
    const spousePersonalLoanTotal = (answers.spousePersonalLoanDetails || []).reduce((sum,d) => sum + Number(d.debtBalance || 0), 0)
    const medicalDebtTotal = (answers.medicalDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0)
    const spouseMedicalDebtTotal = (answers.spouseMedicalDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0)
    const otherDebtTotal = (answers.otherDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0)
    const spouseOtherDebtTotal = (answers.spouseOtherDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0)
    const nonPriorityTaxTotal = Math.max((answers.backTaxDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0) - respondentTaxLast3,0)
    const spouseNonPriorityTaxTotal = Math.max((answers.spouseBackTaxDebtDetails || []).reduce((sum,d) => sum + Number(d.amountOwed || 0), 0) - spouseTaxLast3,0)

    const unsecuredTotal =
      creditCardTotal +
      spouseCreditCardTotal +
      personalLoanTotal +
      spousePersonalLoanTotal +
      medicalDebtTotal +
      spouseMedicalDebtTotal +
      otherDebtTotal +
      spouseOtherDebtTotal +
      nonPriorityTaxTotal +
      spouseNonPriorityTaxTotal;

    const unsecuredItems = {
        creditCardTotal,
        spouseCreditCardTotal,
        personalLoanTotal,
        spousePersonalLoanTotal,
        medicalDebtTotal,
        spouseMedicalDebtTotal,
        otherDebtTotal,
        spouseOtherDebtTotal,
        nonPriorityTaxTotal,
        spouseNonPriorityTaxTotal,
    }

    // Step C: Liquidation limitation.
    const analyses = computeLiquidationValue(answers);

    const totalLiquidationValue = analyses.reduce((sum, analysis) => {
        return sum + (Number(analysis.liquidationValue) || 0);
        }, 0);

    const disposableIncomeResults = computeDisposableIncome(answers);
    const disposableIncomeAvailableToUnsecured = disposableIncomeResults['disposableIncome'] * planLength;
    const unsecuredPlanRequirement = Math.max(disposableIncomeAvailableToUnsecured, totalLiquidationValue);
    const unsecuredPlan = Math.min(unsecuredPlanRequirement,unsecuredTotal)
    const refundAllowed = Number(disposableIncomeResults.breakdown.householdSize) * 500
    // Sum all plan-funded debts
    const planFundedSum = securedTotal + priorityTotal + unsecuredPlan;
  
    // final monthly payment
    const trusteeFee = planFundedSum * (trusteeFactor - 1)
    const monthlyPayment =
      (planFundedSum * trusteeFactor) / planLength;
  
    return {
      planLength,
      trusteeFactor,
      securedTotal,
      mortgageItems,
      mortgageTotalInPlan,
      vehicleItems,
      vehicleTotalInPlan,
      priorityItems,
      totalAttorneyFee,
      attorneyBalance,
      priorityTotal,
      unsecuredItems,
      unsecuredTotal,
      totalLiquidationValue,
      unsecuredPlan,
      grossPlanFunded: planFundedSum,
      trusteeFee,
      monthlyPayment: Math.round(monthlyPayment),
      analyses,
      disposableIncomeResults,
      refundAllowed,
      district,
      adminExpenseMultiplier,
    };
  }
  
